import { useState, useEffect, useRef } from "react";
import { useSiteContent } from "../../../context/SiteContentContext";

// ── Original hardcoded slides — used as fallback if WP has no slides ─────────
const DEFAULT_SLIDES = [
  {
    id: 1,
    tag: "NYC's Trusted HVAC Experts",
    title: "Trusted Experts",
    titleLine2: "in Heating,",
    titleLine3: "Cooling & Controls",
    description: "Thermal Xpert specializes in creating comfortable, energy-efficient homes and businesses across New York City. Family-owned, personalized care on every project.",
    btnPrimary: { label: "Our Services", href: "#control" },
    btnSecondary: { label: "Get a Free Quote", href: "#easy" },
    bg: "https://thermalxpert.nyc/wp-content/uploads/2026/04/d1345a4818e766220f9cace87712516f-scaled.jpg",
  },
  {
    id: 2,
    tag: "Unico System Specialists",
    title: "Small Duct,",
    titleLine2: "Small Machine",
    titleLine3: "for Big Home",
    description: "The ductwork of the Unico System is so small and flexible that it fits right into existing home constructions, weaving through ceilings, walls, and floors.",
    btnPrimary: { label: "Read More", href: "https://thermalxpert.nyc/?p=1777" },
    btnSecondary: { label: "Get a Free Quote", href: "#easy" },
    bg: "https://thermalxpert.nyc/wp-content/uploads/2026/04/d1345a4818e766220f9cace87712516f-scaled.jpg",
  },
  {
    id: 3,
    tag: "NYSERDA & ConEd Partner",
    title: "Home Energy",
    titleLine2: "Efficiency",
    titleLine3: "Programs",
    description: "Improve your home's energy efficiency with Thermal Xpert, NYSERDA, and ConEd. Take advantage of New York State incentives offering up to $10,000 for heat pump installations.",
    btnPrimary: null,
    btnSecondary: { label: "Get a Free Quote", href: "#easy" },
    bg: "https://thermalxpert.nyc/wp-content/uploads/2026/04/d1345a4818e766220f9cace87712516f-scaled.jpg",
  },
  {
    id: 4,
    tag: "Smart Home Automation",
    title: "Smart Solutions",
    titleLine2: "for Modern,",
    titleLine3: "Efficient Homes",
    description: "Advanced automation and control systems designed for comfort and reliability — the same equipment trusted by NYC skyscrapers, made affordable for your home.",
    btnPrimary: null,
    btnSecondary: { label: "Get a Free Quote", href: "#easy" },
    bg: "https://thermalxpert.nyc/wp-content/uploads/2026/04/d1345a4818e766220f9cace87712516f-scaled.jpg",
  },
];

const stats = [
  { value: "15+", label: "Years Experience", icon: "ri-award-fill" },
  { value: "2K+", label: "Projects Done", icon: "ri-building-2-fill" },
  { value: "100%", label: "Satisfaction", icon: "ri-star-fill" },
  { value: "24/7", label: "Support", icon: "ri-customer-service-2-fill" },
];

export default function HeroSlider() {
  const { content } = useSiteContent();
  // Use WP slides if they exist and have entries, otherwise fall back to originals
  const wpSlides = content.hero_slides;
  const slides: typeof DEFAULT_SLIDES = (wpSlides && wpSlides.length > 0)
    ? wpSlides.map((s: any, i: number) => ({
        id:           s.id          ?? i + 1,
        tag:          s.tag         || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].tag,
        title:        s.title       || s.headline?.split("\n")[0] || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].title,
        titleLine2:   s.titleLine2  || s.headline?.split("\n")[1] || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].titleLine2,
        titleLine3:   s.titleLine3  || s.headline?.split("\n")[2] || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].titleLine3,
        description:  s.description || s.sub || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].description,
        btnPrimary:   s.btnPrimary  || s.cta1  || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].btnPrimary,
        btnSecondary: s.btnSecondary || s.cta2 || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].btnSecondary,
        bg:           s.bg          || s.image || DEFAULT_SLIDES[i % DEFAULT_SLIDES.length].bg,
      }))
    : DEFAULT_SLIDES;

  const [current, setCurrent] = useState(0);
  const [animating, setAnimating] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const goTo = (idx: number) => {
    if (animating) return;
    setAnimating(true);
    setTimeout(() => {
      setCurrent(idx);
      setAnimating(false);
    }, 400);
  };

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6500);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [slides.length]);

  const scrollTo = (href: string) => {
    if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.open(href, "_blank");
    }
  };

  const slide = slides[current] ?? slides[0];

  if (!slide) return null;

  const btnPrimary   = slide.btnPrimary;
  const btnSecondary = slide.btnSecondary;
  const slideTitle   = slide.title;
  const titleLine2   = slide.titleLine2;
  const titleLine3   = slide.titleLine3;
  const description  = slide.description;

  return (
    <section id="home" className="w-full relative overflow-hidden" style={{ minHeight: "100vh", height: "100vh" }}>
      {/* Background images — use background-image so they fill correctly */}
      {slides.map((s, i) => (
        <div
          key={s.id}
          className="absolute inset-0 transition-opacity duration-700"
          style={{
            opacity: i === current ? 1 : 0,
            zIndex: 0,
            backgroundImage: `url(${s.bg})`,
            backgroundSize: "cover",
            backgroundPosition: "center center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="absolute inset-0" style={{ background: "linear-gradient(105deg,rgba(13,40,80,0.82) 0%,rgba(13,40,80,0.65) 45%,rgba(13,40,80,0.25) 100%)" }} />
          <div className="absolute inset-0" style={{ background: "linear-gradient(to top,rgba(13,40,80,0.6) 0%,transparent 50%)" }} />
        </div>
      ))}

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-8 flex flex-col lg:flex-row items-center min-h-screen py-24 gap-12">
        {/* Left — text */}
        <div className="flex-1 flex flex-col gap-7">
          <div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold w-fit"
            style={{ background: "rgba(74,159,212,0.25)", border: "1px solid rgba(74,159,212,0.5)", color: "#7dd3fc" }}
          >
            <span className="w-1.5 h-1.5 rounded-full animate-pulse bg-sky-300"></span>
            {slide.tag}
          </div>

          <div
            style={{
              opacity: animating ? 0 : 1,
              transform: animating ? "translateY(14px)" : "translateY(0)",
              transition: "opacity 0.4s ease, transform 0.4s ease",
            }}
          >
            <h1 className="font-black leading-none tracking-tight text-white" style={{ fontSize: "clamp(3rem,5.5vw,5rem)" }}>
              <span className="block">{slideTitle}</span>
              <span className="block">{titleLine2}</span>
              <span className="block" style={{ color: "#7dd3fc" }}>{titleLine3}</span>
            </h1>
          </div>

          <p
            className="text-base leading-relaxed max-w-lg text-white/75"
            style={{ opacity: animating ? 0 : 1, transition: "opacity 0.4s ease 0.06s" }}
          >
            {description}
          </p>

          <div className="flex items-center gap-4 flex-wrap">
            {btnPrimary && (
              <button
                onClick={() => scrollTo(btnPrimary.href)}
                className="px-7 py-3.5 rounded-full text-sm font-bold whitespace-nowrap cursor-pointer transition-all hover:scale-105 border-2 text-white"
                style={{ borderColor: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.1)", backdropFilter: "blur(8px)" }}
              >
                {btnPrimary.label}
              </button>
            )}
            {btnSecondary && (
              <button
                onClick={() => scrollTo(btnSecondary.href)}
                className="px-7 py-3.5 rounded-full text-sm font-bold text-white whitespace-nowrap cursor-pointer transition-all hover:scale-105 hover:opacity-90 flex items-center gap-2"
                style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}
              >
                {btnSecondary.label}
                <i className="ri-arrow-right-line"></i>
              </button>
            )}
          </div>

          <div className="flex items-center gap-2 mt-2">
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className="rounded-full transition-all duration-300 cursor-pointer"
                style={{
                  width: i === current ? "32px" : "8px",
                  height: "8px",
                  background: i === current ? "#4a9fd4" : "rgba(255,255,255,0.3)",
                }}
              />
            ))}
          </div>
        </div>

        {/* Right — floating info card */}
        <div className="hidden lg:flex flex-col gap-4 w-72 flex-shrink-0">
          <div
            className="rounded-3xl p-6 flex flex-col gap-5"
            style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.2)" }}
          >
            <p className="text-xs font-bold uppercase tracking-widest text-white/60">Our Track Record</p>
            <div className="grid grid-cols-2 gap-4">
              {stats.map((s) => (
                <div key={s.label} className="flex flex-col gap-1">
                  <div className="w-9 h-9 flex items-center justify-center rounded-xl mb-1" style={{ background: "rgba(74,159,212,0.3)" }}>
                    <i className={`${s.icon} text-sm text-sky-300`}></i>
                  </div>
                  <span className="text-2xl font-black text-white">{s.value}</span>
                  <span className="text-xs text-white/50 leading-tight">{s.label}</span>
                </div>
              ))}
            </div>
          </div>

          <div
            className="rounded-2xl p-4 flex items-center gap-3"
            style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.2)" }}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "linear-gradient(135deg,#4a9fd4,#2d7ab5)" }}>
              <i className="ri-shield-check-fill text-white text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-black text-white">Licensed &amp; Insured</p>
              <p className="text-xs text-white/50">NYC Certified Contractor</p>
            </div>
          </div>

          <div
            className="rounded-2xl p-4 flex items-center gap-3"
            style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.2)" }}
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "rgba(74,159,212,0.3)" }}>
              <i className="ri-leaf-fill text-sky-300 text-lg"></i>
            </div>
            <div>
              <p className="text-sm font-black text-white">NYSERDA Partner</p>
              <p className="text-xs text-white/50">Up to $10K in rebates</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom wave shape */}
      <div className="absolute bottom-0 left-0 right-0 z-10 pointer-events-none" style={{ height: "80px" }}>
        <svg viewBox="0 0 1440 80" preserveAspectRatio="none" className="w-full h-full" fill="#f7fbff">
          <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" />
        </svg>
      </div>
    </section>
  );
}
